import React, { useState, useEffect, useMemo } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import { sleep } from "../shared/util";
import { Container, Image } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import useVersion from "../shared/useVersion";
import "./SayOneSound.scss";
import icon from "./sayonesound-icon.png";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen } from "Services/Listen";
import { getUserData } from "Services/API";
import checkmark from "./checkmark.png";
import { ipaToAmirabet } from "../../../app/services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function SayOneSoundValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.say_one_sound;

  const { id } = useVersion(props.wordMetadata, {
    1: ["PHON", "GRAPHEMIC_BREAKDOWN", "IMG_URL"],
  });

  const versionId = props.version || id;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!versionId) close();
  }, [versionId]);

  if (!versionId) return null;

  const Version = {
    1: SayOneSound,
  }[versionId];

  return <Version {...props} close={close} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function SayOneSound(props) {
  const { word, wordMetadata, speech, ftue } = props;
  const { PHON, GRAPHEMIC_BREAKDOWN, IMG_URL } = wordMetadata;
  const graphemes = GRAPHEMIC_BREAKDOWN.split("/");
  const abPhonemes = ipaToAmirabet(PHON);

  const [stage, setStage] = useState(0);
  const [colorIndex, setColorIndex] = useState(-1);
  const [showCheck, setShowCheck] = useState(false);

  const { speak, speakSequence, speakFile, listenPhoneme, listenWord } = useConversation(
    speech,
    listen
  );
  const { cancellablePromise } = useCancellablePromise();

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  // boolean, true if using first sound, false if last sound
  const first = useMemo(() => Math.random() < 0.5, []);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0: {
          if (ftue) {
            await cancellablePromise(speak(ttsStrings.intro));
            setColorIndex(-2);
            await cancellablePromise(sleep());
            await cancellablePromise(speak(word));
            await cancellablePromise(sleep());
            setColorIndex(-1);
            await cancellablePromise(sleep());
          }
          const p1 = first ? ttsStrings.last : ttsStrings.first;
          const i = first ? graphemes.length - 1 : 0;
          setColorIndex(i);

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.describe({ position: p1 }),
              {textToSpeak: word, skipTranslation: true},
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.describe({ position: p1 })} ${word}`));

          await cancellablePromise(sleep());
          await cancellablePromise(speakFile(files[i]));
          setShowCheck(true);
          await cancellablePromise(sleep(2000));
          setShowCheck(false);
          setColorIndex(-1);

          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.ask_word,
              {textToSpeak: word, skipTranslation: true},
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.ask_word} ${word}`));

          setColorIndex(-2);
          const { status: s1 } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle"],
          }));
          if (s1 === "skip") return props.close();
          setColorIndex(-1);
          if (s1 === "struggle") {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.struggle,
                {textToSpeak: word, skipTranslation: true},
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.struggle} ${word}`));
          }
          const p2 = first ? ttsStrings.first : ttsStrings.last;
          const j = first ? 0 : graphemes.length - 1;
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.ask_sound({ position: p2 }),
              {textToSpeak: word, skipTranslation: true},
            ]));
          }
          else await cancellablePromise(speak(`${ttsStrings.ask_sound({ position: p2 })} ${word}`));

          setColorIndex(j);
          const { status: s2 } = await cancellablePromise(listenPhoneme({ file: files[j], targetWord: abPhonemes[j] }));
          if (s2 === "skip") return props.close();
          setShowCheck(true);
          await cancellablePromise(speak(ttsStrings.sound_is));
          await cancellablePromise(sleep(500));
          await cancellablePromise(speakFile(files[j]));
          props.close();
          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      <div style={{ marginTop: 30 }}>
        <ListeningStatus />
      </div>
    </Container>
  );

  function renderContent() {
    return (
      <div className="say-one-sound">
        <Image src={IMG_URL} style={{ width: 320, borderRadius: 0 }} />
        <div className="phoneme-bar">
          {graphemes.map((g, i) => {
            const style = {};
            const match = colorIndex === i || colorIndex === -2;
            if (match) style.background = "#FFDC00";
            return (
              <div key={i} className="phoneme-box" style={style}>
                {match && showCheck && (
                  <img className="checkmark" src={checkmark} alt="match checkmark" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
